// Theme configuration for GuapGoals
// Supports multiple dark mode variants

export const THEMES = {
  light: {
    id: 'light',
    name: 'Light Mode',
    emoji: '☀️',
    colors: {
      background: '#f9fafb',
      surface: '#ffffff',
      surfaceHover: '#f3f4f6',
      text: '#111827',
      textSecondary: '#6b7280',
      border: '#e5e7eb',
      primary: '#10b981',
      primaryHover: '#059669',
      accent: '#CCFF00',
    }
  },
  dark: {
    id: 'dark',
    name: 'Dark Mode',
    emoji: '🌙',
    colors: {
      background: '#0f0f0f',
      surface: '#1a1a1a',
      surfaceHover: '#262626',
      text: '#ffffff',
      textSecondary: '#9ca3af',
      border: '#333333',
      primary: '#CCFF00',
      primaryHover: '#b8e600',
      accent: '#CCFF00',
    }
  },
  midnight: {
    id: 'midnight',
    name: 'Midnight Blue',
    emoji: '🌌',
    colors: {
      background: '#0a0a1a',
      surface: '#12122a',
      surfaceHover: '#1a1a3a',
      text: '#e0e7ff',
      textSecondary: '#818cf8',
      border: '#312e81',
      primary: '#6366f1',
      primaryHover: '#4f46e5',
      accent: '#818cf8',
    }
  },
  rose: {
    id: 'rose',
    name: 'Rose Gold',
    emoji: '🌹',
    colors: {
      background: '#1a0a0a',
      surface: '#2a1212',
      surfaceHover: '#3a1a1a',
      text: '#fce7f3',
      textSecondary: '#f9a8d4',
      border: '#831843',
      primary: '#ec4899',
      primaryHover: '#db2777',
      accent: '#f472b6',
    }
  },
  money: {
    id: 'money',
    name: 'Money Green',
    emoji: '💵',
    colors: {
      background: '#0a1a0a',
      surface: '#0d260d',
      surfaceHover: '#103310',
      text: '#dcfce7',
      textSecondary: '#86efac',
      border: '#166534',
      primary: '#22c55e',
      primaryHover: '#16a34a',
      accent: '#4ade80',
    }
  }
};

// Get CSS variables for a theme
export function getThemeCSSVariables(themeId) {
  const theme = THEMES[themeId] || THEMES.light;
  return {
    '--bg-primary': theme.colors.background,
    '--bg-surface': theme.colors.surface,
    '--bg-surface-hover': theme.colors.surfaceHover,
    '--text-primary': theme.colors.text,
    '--text-secondary': theme.colors.textSecondary,
    '--border-color': theme.colors.border,
    '--color-primary': theme.colors.primary,
    '--color-primary-hover': theme.colors.primaryHover,
    '--color-accent': theme.colors.accent,
  };
}

// Apply theme to document
export function applyTheme(themeId) {
  const variables = getThemeCSSVariables(themeId);
  const root = document.documentElement;
  
  Object.entries(variables).forEach(([key, value]) => {
    root.style.setProperty(key, value);
  });
  
  // Set data attribute for CSS selectors
  root.setAttribute('data-theme', themeId);
  
  // Save preference
  localStorage.setItem('guapgoals-theme', themeId);
}

// Get saved theme or default
export function getSavedTheme() {
  return localStorage.getItem('guapgoals-theme') || 'light';
}

// Check if current theme is dark
export function isDarkTheme(themeId) {
  return themeId !== 'light';
}

// Get system preferred theme
export function getSystemTheme() {
  if (typeof window === 'undefined') return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

// Apply system theme preference
export function applySystemTheme() {
  const systemTheme = getSystemTheme();
  applyTheme(systemTheme);
}

// Initialize theme based on saved mode preference
export function initializeTheme() {
  const savedMode = localStorage.getItem('guapgoals-theme-mode');
  
  if (savedMode === 'system' || !savedMode) {
    // Default to system preference
    applySystemTheme();
  } else {
    // Apply saved theme (light or dark)
    applyTheme(savedMode);
  }
}

export default THEMES;
